import React, { useState } from 'react';

/**
 * QuantityInput — a reusable +/- stepper input for quantity/price fields.
 * Props:
 *   value (string|number): controlled value
 *   onChange (fn): called with new string value
 *   onBlur (fn): optional blur handler
 *   min (number): minimum value (default 0)
 *   max (number): maximum value (optional)
 *   step (number): step for +/- buttons (default 1)
 *   placeholder (string)
 *   label (string): optional label above the input
 *   error (string): error message shown below
 *   prefix (string): optional prefix text inside input (e.g. "₹")
 *   suffix (string): optional suffix text (e.g. "MT")
 *   className (string): extra classes for wrapper
 *   inputClassName (string): extra classes for input element
 *   disabled (boolean)
 */
const QuantityInput = ({
  value,
  onChange,
  onBlur,
  min = 0,
  max,
  step = 1,
  placeholder = '0',
  label,
  error,
  prefix,
  suffix,
  className = '',
  inputClassName = '',
  disabled = false,
}) => {
  const numVal = parseFloat(value) || 0;

  const handleDecrement = () => {
    const next = Math.max(min, numVal - step);
    onChange(String(next));
  };

  const handleIncrement = () => {
    const next = max !== undefined ? Math.min(max, numVal + step) : numVal + step;
    onChange(String(next));
  };

  const handleChange = (e) => {
    const raw = e?.target?.value;
    // Allow empty string, digits, and one decimal point
    if (raw === '' || /^\d*\.?\d*$/?.test(raw)) {
      onChange(raw);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-semibold mb-2" style={{ color: '#1f2937' }}>
          {label}
        </label>
      )}
      <div
        className={`flex items-stretch rounded-xl overflow-hidden border-2 transition-colors ${
          error
            ? 'border-red-500' :'border-amber-400 focus-within:border-amber-500'
        } shadow-sm`}
      >
        {/* Decrement */}
        <button
          type="button"
          onClick={handleDecrement}
          disabled={disabled || numVal <= min}
          className="flex items-center justify-center w-12 min-h-[48px] bg-gray-800 hover:bg-gray-700 disabled:bg-gray-300 text-white text-2xl font-bold transition-colors select-none active:scale-95 flex-shrink-0"
          aria-label="Decrease"
        >
          −
        </button>

        {/* Input area */}
        <div className="flex-1 flex items-center bg-white dark:bg-gray-800 px-2 min-w-0">
          {prefix && (
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-1 flex-shrink-0">{prefix}</span>
          )}
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={`flex-1 text-center text-lg font-bold bg-transparent text-gray-900 dark:text-white border-0 outline-none min-w-0 py-3 ${inputClassName}`}
          />
          {suffix && (
            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 ml-1 flex-shrink-0">{suffix}</span>
          )}
        </div>

        {/* Increment */}
        <button
          type="button"
          onClick={handleIncrement}
          disabled={disabled || (max !== undefined && numVal >= max)}
          className="flex items-center justify-center w-12 min-h-[48px] bg-amber-500 hover:bg-amber-400 disabled:bg-gray-300 text-white text-2xl font-bold transition-colors select-none active:scale-95 flex-shrink-0"
          aria-label="Increase"
        >
          +
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 font-medium flex items-center gap-1">
          ⚠ {error}
        </p>
      )}
    </div>
  );
};

export default QuantityInput;
